import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  Map, Database, Mountain, Globe, Navigation2, Building2,
  Layers, GitMerge, History, Upload, BarChart3,
  LogOut, ChevronLeft, ChevronRight, Shield, Crosshair,
  CheckSquare, Activity, Palette,
} from 'lucide-react';
import { useAuth, useAuthStore } from '../store/useAuthStore';
import { useTheme, THEMES } from '../contexts/ThemeContext';
import LogoWordmark from '../components/LogoWordmark';
import './AdminGeoLayout.css';

/* ── Map layer context (consommé par les pages avec carte) ── */
export const ADMIN_TILE_LAYERS = {
  dark:      { label: 'Sombre',    color: '#818cf8', url: 'https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}',  attribution: '© Esri', className: 'imap-tiles-dark' },
  osm:       { label: 'Standard',  color: '#10b981', url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',              attribution: '© OpenStreetMap contributors' },
  satellite: { label: 'Satellite', color: '#f59e0b', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attribution: 'Tiles © Esri' },
};
const MapLayerContext = createContext('dark');
export function useMapLayer() { return useContext(MapLayerContext); }

const NAV = [
  {
    label: 'Vue d\'ensemble',
    items: [
      { to: '/admin-geo',           icon: BarChart3,   label: 'Tableau de bord', end: true },
    ],
  },
  {
    label: 'Données géodésiques',
    items: [
      { to: '/admin-geo/geodesy',   icon: Crosshair,   label: 'Points géodésiques', badge: null },
      { to: '/admin-geo/altimetrie',icon: Mountain,     label: 'Repères altimétriques' },
    ],
  },
  {
    label: 'Données administratives',
    items: [
      { to: '/admin-geo/limites',   icon: Globe,       label: 'Limites administratives' },
      { to: '/admin-geo/routes',    icon: Navigation2, label: 'Réseaux routiers' },
      { to: '/admin-geo/batiments', icon: Building2,   label: 'Bâtiments' },
    ],
  },
  {
    label: 'Gestion couches',
    items: [
      { to: '/admin-geo/couches',       icon: Layers,    label: 'Couches SIG' },
      { to: '/admin-geo/referentiels',  icon: Database,  label: 'Référentiels' },
    ],
  },
  {
    label: 'Outils & Qualité',
    items: [
      { to: '/admin-geo/validation', icon: CheckSquare, label: 'Validation topologique' },
      { to: '/admin-geo/import',     icon: Upload,      label: 'Import / Export' },
      { to: '/admin-geo/audit',      icon: History,     label: 'Historique' },
    ],
  },
];

function ThemeSwitcher() {
  const { themeId, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        className={`ag-tool-btn${open ? ' active' : ''}`}
        onClick={() => setOpen(v => !v)}
        title="Changer de thème"
      >
        <Palette size={13} /> Thème
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 500,
          background: 'var(--ag-surface)', border: '1px solid var(--ag-border-dk)',
          borderRadius: 10, padding: 6, minWidth: 150,
          boxShadow: '0 8px 30px rgba(0,0,0,0.5)',
        }}>
          {Object.values(THEMES).map(t => (
            <button
              key={t.id}
              onClick={() => { setTheme(t.id); setOpen(false); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 9,
                width: '100%', padding: '7px 10px', borderRadius: 6,
                border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 500,
                background: themeId === t.id ? 'var(--ag-accent-actv)' : 'transparent',
                color: themeId === t.id ? 'var(--ag-green)' : 'var(--ag-text)',
                transition: 'background .12s',
              }}
              onMouseEnter={e => { if (themeId !== t.id) e.currentTarget.style.background = 'var(--ag-accent-hover)'; }}
              onMouseLeave={e => { if (themeId !== t.id) e.currentTarget.style.background = 'transparent'; }}
            >
              <span style={{
                width: 12, height: 12, borderRadius: '50%', flexShrink: 0,
                background: t.swatch,
                boxShadow: themeId === t.id ? `0 0 6px ${t.swatch}` : 'none',
              }} />
              {t.label}
              {themeId === t.id && (
                <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--ag-green)' }}>✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MapLayerSwitcher({ activeLayer, setActiveLayer }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const current = ADMIN_TILE_LAYERS[activeLayer];

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        className={`ag-tool-btn${open ? ' active' : ''}`}
        onClick={() => setOpen(v => !v)}
        title="Changer de fond de carte"
      >
        <Map size={13} />
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: current.color, display: 'inline-block', marginLeft: 2 }} />
        {current.label}
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 500,
          background: 'var(--ag-surface)', border: '1px solid var(--ag-border-dk)',
          borderRadius: 10, padding: 6, minWidth: 150,
          boxShadow: '0 8px 30px rgba(0,0,0,0.5)',
        }}>
          {Object.entries(ADMIN_TILE_LAYERS).map(([key, tile]) => (
            <button
              key={key}
              onClick={() => { setActiveLayer(key); setOpen(false); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 9,
                width: '100%', padding: '7px 10px', borderRadius: 6,
                border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 500,
                background: activeLayer === key ? 'var(--ag-accent-actv)' : 'transparent',
                color: activeLayer === key ? 'var(--ag-green)' : 'var(--ag-text)',
                transition: 'background .12s',
              }}
              onMouseEnter={e => { if (activeLayer !== key) e.currentTarget.style.background = 'var(--ag-accent-hover)'; }}
              onMouseLeave={e => { if (activeLayer !== key) e.currentTarget.style.background = 'transparent'; }}
            >
              <span style={{
                width: 12, height: 12, borderRadius: '50%', flexShrink: 0,
                background: tile.color,
                boxShadow: activeLayer === key ? `0 0 6px ${tile.color}` : 'none',
              }} />
              {tile.label}
              {activeLayer === key && (
                <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--ag-green)' }}>✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function NavItem({ to, icon: Icon, label, badge, collapsed, end }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) => `ag-nav-link${isActive ? ' active' : ''}`}
    >
      <Icon size={15} className="ag-nav-icon" />
      <span className="ag-nav-label">{label}</span>
      {badge !== undefined && badge !== null && !collapsed && (
        <span className="ag-nav-badge">{badge}</span>
      )}
    </NavLink>
  );
}

export default function AdminGeoLayout() {
  const navigate  = useNavigate();
  const { user }  = useAuth();
  const { themeId } = useTheme();
  const [collapsed,    setCollapsed]   = useState(false);
  const [coords,       setCoords]      = useState({ lat: '-18.9100', lng: '47.5362' });
  const [activeLayer,  setActiveLayer] = useState('dark');

  useEffect(() => {
    setActiveLayer(prev => {
      if (prev === 'satellite') return prev;
      return themeId === 'light' ? 'osm' : 'dark';
    });
  }, [themeId]);

  const initials = (user?.first_name?.[0] || '') + (user?.last_name?.[0] || '') || 'AG';

  useEffect(() => {
    const id = setInterval(() => {
      setCoords({
        lat: (-18.9100 + (Math.random() - 0.5) * 0.0001).toFixed(6),
        lng: (47.5362  + (Math.random() - 0.5) * 0.0001).toFixed(6),
      });
    }, 3000);
    return () => clearInterval(id);
  }, []);

  const handleLogout = () => {
    useAuthStore.logout();
    navigate('/login');
  };

  return (
    <div className="ag-root">

      {/* ══ TOP BAR ══════════════════════════════════════════════ */}
      <header className="ag-topbar">

        {/* Brand */}
        <div className={`ag-topbar-brand${collapsed ? ' collapsed' : ''}`}>
          <div className="ag-topbar-logo">
            <Shield size={14} color="#040d18" />
          </div>
          <div className="ag-topbar-title">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <LogoWordmark height={15} madColor={themeId === 'light' ? '#0f172a' : undefined} /> SIG
            </span>
            <span className="badge-beta">BETA</span>
            <small>Admin Géospatial</small>
          </div>
        </div>

        {/* Outils */}
        <div className="ag-topbar-tools">
          <MapLayerSwitcher activeLayer={activeLayer} setActiveLayer={setActiveLayer} />
          <div className="ag-tool-sep" />
          <button className="ag-tool-btn">
            <Layers size={13} /> Couches
          </button>
          <button className="ag-tool-btn">
            <GitMerge size={13} /> Analyse
          </button>
          <button className="ag-tool-btn">
            <Upload size={13} /> Importer
          </button>
          <div className="ag-tool-sep" />
          <button className="ag-tool-btn">
            <Activity size={13} /> Stats live
          </button>
          <div className="ag-tool-sep" />
          <ThemeSwitcher />
        </div>

        {/* Coordonnées */}
        <div className="ag-topbar-coords">
          <Crosshair size={11} />
          <span>Lat <span className="ag-coords-live">{coords.lat}</span></span>
          <span>Lng <span className="ag-coords-live">{coords.lng}</span></span>
          <span>WGS84</span>
        </div>

        {/* User */}
        <div className="ag-topbar-user">
          <div className="ag-topbar-user-avatar">{initials}</div>
          <div className="ag-topbar-user-info">
            <div className="ag-topbar-user-name">
              {user?.first_name || user?.email?.split('@')[0] || 'Admin'}
            </div>
            <div className="ag-topbar-user-role">Admin Géo</div>
          </div>
          <button className="ag-topbar-btn" onClick={handleLogout} title="Déconnexion">
            <LogOut size={14} />
          </button>
        </div>
      </header>

      {/* ══ SIDE NAV ═════════════════════════════════════════════ */}
      <nav className={`ag-sidenav${collapsed ? ' collapsed' : ''}`}>
        {NAV.map(group => (
          <div key={group.label} className="ag-sidenav-section">
            <div className="ag-sidenav-section-label">{group.label}</div>
            {group.items.map(item => (
              <NavItem key={item.to} {...item} collapsed={collapsed} />
            ))}
          </div>
        ))}

        <div className="ag-sidenav-spacer" />

        <NavLink to="/expert" className="ag-nav-link" style={{ marginBottom: 4 }}>
          <Globe size={15} className="ag-nav-icon" />
          <span className="ag-nav-label">Espace utilisateur</span>
        </NavLink>

        <button className="ag-sidenav-collapse-btn" onClick={() => setCollapsed(v => !v)}>
          {collapsed ? <ChevronRight size={14} /> : <><ChevronLeft size={14} /><span>Réduire</span></>}
        </button>
      </nav>

      {/* ══ MAIN CONTENT ═════════════════════════════════════════ */}
      <main className="ag-main">
        <MapLayerContext.Provider value={activeLayer}>
          <Outlet />
        </MapLayerContext.Provider>
      </main>

      {/* ══ STATUS BAR ═══════════════════════════════════════════ */}
      <footer className="ag-statusbar">
        <div className="ag-status-item">
          <div className="ag-status-dot" />
          <span>Connecté</span>
        </div>
        <div className="ag-status-item">
          <span>CRS: <span className="ag-status-val">EPSG:4326</span></span>
        </div>
        <div className="ag-status-item">
          <span>Laborde: <span className="ag-status-val">EPSG:29701</span></span>
        </div>
        <div className="ag-status-item">
          <span>BD: <span className="ag-status-val">madjeo_db</span></span>
        </div>
        <div className="ag-status-spacer" />
        <div className="ag-status-item">
          <span>Madjeo SIG v1.0</span>
        </div>
      </footer>

    </div>
  );
}
